import { Link } from 'react-router-dom';
import { Wallet } from 'lucide-react';

interface EnvelopeData {
  id: number;
  name: string;
  budgeted: number;
  spent: number;
  remaining: number;
  percentage: number;
}

interface EnvelopeSummaryProps {
  envelopes: EnvelopeData[];
  isLoading?: boolean;
}

export default function EnvelopeSummary({ envelopes, isLoading }: EnvelopeSummaryProps) {
  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-between pb-5 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-text-header">Envelope Status</h3>
            <p className="text-sm text-text-muted">This month</p>
          </div>
        </div>
        <div className="pt-6 space-y-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center justify-between mb-2">
                <div className="h-4 bg-background-subtle rounded w-1/3" />
                <div className="h-4 bg-background-subtle rounded w-20" />
              </div>
              <div className="h-2 bg-background-subtle rounded" />
              <div className="flex items-center justify-between mt-2">
                <div className="h-3 bg-background-subtle rounded w-24" />
                <div className="h-3 bg-background-subtle rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (envelopes.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center justify-between pb-5 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-text-header">Envelope Status</h3>
            <p className="text-sm text-text-muted">This month</p>
          </div>
        </div>
        <div className="pt-10 pb-8 text-center">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 border border-primary/20">
            <Wallet className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm text-text-muted mb-5">No envelopes created yet.</p>
          <Link to="/envelopes" className="btn btn-primary inline-flex">
            Create envelopes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between pb-5 border-b border-border">
        <div>
          <h3 className="text-lg font-semibold text-text-header">Envelope Status</h3>
          <p className="text-sm text-text-muted">This month</p>
        </div>
        <Link to="/envelopes" className="text-sm font-medium text-primary hover:text-primary-hover transition-colors">
          View all
        </Link>
      </div>

      <div className="pt-6 space-y-5">
        {envelopes.map((envelope) => {
          const progress = Math.min(envelope.percentage, 100);
          const progressColor = getProgressColor(envelope.percentage);
          const remainingTone = envelope.remaining >= 0 ? 'text-text-muted' : 'text-danger';

          return (
            <div
              key={envelope.id}
              className="p-3 rounded-lg border border-transparent hover:border-border hover:bg-background-subtle/40 transition-all duration-200"
            >
              <div className="flex items-center justify-between gap-4 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-header truncate">{envelope.name}</p>
                </div>
                <p className={`text-sm font-medium ${remainingTone}`}>{formatCurrency(envelope.remaining)} left</p>
              </div>

              <div className="w-full bg-background-subtle rounded-full h-2 overflow-hidden border border-border">
                <div className={`h-2 rounded-full transition-all duration-200 ${progressColor}`} style={{ width: `${progress}%` }} />
              </div>

              <div className="flex items-center justify-between mt-2 text-xs text-text-muted">
                <span>{formatCurrency(envelope.spent)} spent</span>
                <span>of {formatCurrency(envelope.budgeted)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getProgressColor(percentage: number): string {
  if (percentage >= 100) return 'bg-danger';
  if (percentage >= 80) return 'bg-warning';
  return 'bg-primary';
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
